import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { get } from 'lodash';
import { Show } from '../../show';

import GridContainer from '../../layouts/components/grid-container';
import FluidContainer from '../../layouts/components/fluid-container';

import { PublicTitle, Question, Attachments } from '../components';

import { Body } from '../../typography';
import AudioPlayer from '../../audio-player';
import ImageGallery from '../../image-gallery';
import SharedText from '../../shared-text';
import YouTubePlayer from '../../video-player/youtube';
import Geogebra from '../../geogebra';
import Spacer from '../../spacer';
import Stack from '../../stack';
import FinalAnswer from './final-answer';
import Execution from './execution';
import MathContent from '../../math-content';

import ExistenceConditions from './existence-conditions';

import { exerciseDefaultProps, exercisePropTypes } from '../../../constants/exercise';
import { useOnClickOutside } from '../../../lib/hooks';
import {
    ackSync,
    addCde,
    addStep,
    closeAll,
    deleteCde,
    deleteStep,
    initializeChecker,
    openCde,
    openStep,
    updateCde,
    updateFinalAnswer,
    updateStep,
    validate,
} from '../../../actions/checker';
import { isExerciseWithCdes } from '../../../lib/checker';

const Checker = ({
    answers = exerciseDefaultProps.answers,
    correctable,
    onAnswer,
    exercise,
    onPlayVideo,
    videos,
    show,
    hasAttachments = exerciseDefaultProps.hasAttachments,
    correctionStatus,
}) => {
    const dispatch = useDispatch();
    const checker = useSelector((state) => state.checker);

    const { exerciseId, openedCde, openedStep, cdes = [], steps, finalAnswer, isSynced, isValidated } = checker;

    const hasCdes = isExerciseWithCdes(exercise);
    const showGrid = (hasAttachments?.images || hasAttachments?.video) && show.attachments;

    useEffect(() => {
        if (exerciseId !== exercise.id && isValidated && isSynced) {
            dispatch(initializeChecker(exercise, answers));
        }
    }, [exercise, answers, exerciseId, isValidated, isSynced, dispatch]);

    useEffect(() => {
        if (!isSynced) {
            onAnswer(exerciseId, checker);
            dispatch(ackSync());
        }
    }, [checker, dispatch, exerciseId, onAnswer, isSynced]);

    const exerciseContainer = useRef();
    useOnClickOutside(exerciseContainer, () => {
        dispatch(closeAll());
        dispatch(validate({ exercise }));
    });

    return (
        <>
            <Show when={show.title && exercise.publicTitle}>
                <FluidContainer paddingBottom={24}>
                    <PublicTitle>{exercise.publicTitle}</PublicTitle>
                </FluidContainer>
            </Show>

            <Show when={show.prompt && exercise.prompt}>
                <FluidContainer paddingBottom={24}>
                    <MathContent>{exercise.prompt}</MathContent>
                </FluidContainer>
            </Show>

            {hasAttachments?.geogebra && (
                <FluidContainer paddingBottom={24}>
                    <Geogebra exerciseId={exercise.id} src={exercise.attachments.geogebra.src} />
                </FluidContainer>
            )}

            <Show when={show.sharedText && hasAttachments?.sharedText}>
                <FluidContainer paddingBottom={24} flexWrap="wrap">
                    <SharedText exercise={exercise} />
                </FluidContainer>
            </Show>

            {show.attachments && hasAttachments?.audio && (
                <FluidContainer paddingBottom={24}>
                    <AudioPlayer source={exercise.attachments.audio?.src} />
                </FluidContainer>
            )}

            <GridContainer columns={[1, showGrid ? 2 : 1]} paddingBottom={24} ref={exerciseContainer}>
                <Attachments show={showGrid}>
                    <Stack gap="desktop">
                        {hasAttachments.video && (
                            <YouTubePlayer
                                videoIdOrSource={exercise.attachments.video?.src}
                                onPlayVideo={() => onPlayVideo(exercise.id)}
                                showPreview={!videos.find((item) => item.exerciseId === exercise.id)}
                            />
                        )}
                        {hasAttachments.images && <ImageGallery images={get(exercise, 'attachments.images')} />}
                    </Stack>
                </Attachments>

                <FluidContainer flexDirection="column">
                    <Question>{exercise.question}</Question>

                    <Show when={hasCdes}>
                        <Spacer size="base" />
                        <Body weight="semi-bold">{'Condizioni di esistenza'}</Body>
                        <ExistenceConditions
                            openedCde={openedCde}
                            cdes={cdes}
                            correctionStatus={correctionStatus}
                            exercise={exercise}
                            isCorrectable={correctable}
                            onAdd={(index) => dispatch(addCde({ exercise, cdes, index }))}
                            onChange={(index, value) => dispatch(updateCde(index, value))}
                            onOpen={(index) => dispatch(openCde(index))}
                            onDelete={(index) => dispatch(deleteCde(index))}
                        />
                    </Show>

                    <Spacer size="base" />

                    <Execution
                        openedStep={openedStep}
                        exercise={exercise}
                        correctable={correctable}
                        correctionStatus={correctionStatus}
                        steps={steps}
                        onChange={(index, value) => dispatch(updateStep(index, value))}
                        onDelete={(index) => dispatch(deleteStep(index))}
                        onOpen={(index) => dispatch(openStep(index))}
                        onAdd={(index) => dispatch(addStep({ exercise, steps, index, isCorrectable: correctable }))}
                    />
                </FluidContainer>
            </GridContainer>

            <FinalAnswer
                exercise={exercise}
                finalAnswer={finalAnswer}
                correction={correctionStatus}
                onReplace={(value) => dispatch(updateFinalAnswer(value))}
            />
            <Spacer size="regular" />
        </>
    );
};

Checker.propTypes = exercisePropTypes;

export default Checker;
